import type { LeaveRequest, LeaveRequestEvent } from "@spork/s4p-server-sdk";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { Text } from "@/components/ui/text";
import {
    getLeaveRequestEventDotClassName,
    getLeaveRequestEvents,
    getLeaveRequestStatusChangeDetail,
    isLeaveRequestStatusChangedEvent,
} from "@/features/requests/leaveRequestEvents";
import { formatDisplayDate, formatDisplayDateTime } from "@/lib/date";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/testIdDefinition";

type LeaveRequestProgressTimelineProps = {
    request: LeaveRequest;
};

/** Detail text stored on the event payload (original/resubmit message, or school note). */
function getEventDetailText(
    event: LeaveRequestEvent,
    translateStatus: (status: string) => string,
): string | undefined {
    if (isLeaveRequestStatusChangedEvent(event.type)) {
        return getLeaveRequestStatusChangeDetail(event, translateStatus);
    }

    if (event.type === "info_requested" || event.type === "conditionally_approved") {
        return event.payload?.note?.trim() || undefined;
    }

    if (
        event.type === "created" ||
        event.type === "auto_approved" ||
        event.type === "resubmitted"
    ) {
        return event.payload?.message?.trim() || undefined;
    }

    if (
        event.type === "attachment_added" ||
        event.type === "attachment_removed"
    ) {
        return event.payload?.original_filename?.trim() || undefined;
    }

    if (event.type === "end_date_set") {
        const dateTo = event.payload?.date_to?.trim();
        return dateTo ? formatDisplayDate(dateTo) : undefined;
    }

    return undefined;
}

export function LeaveRequestProgressTimeline({
    request,
}: LeaveRequestProgressTimelineProps) {
    const { t } = useTranslation();
    const events = getLeaveRequestEvents(request);

    if (events.length === 0) {
        return (
            <View className="gap-1">
                <Text variant="caption">
                    {t("leave.submittedAt", {
                        date: formatDisplayDateTime(request.created_at),
                    })}
                </Text>
                {request.message?.trim() ? (
                    <Text variant="muted">{request.message.trim()}</Text>
                ) : null}
                {request.updated_at !== request.created_at ? (
                    <Text variant="caption">
                        {t("leave.updatedAt", {
                            date: formatDisplayDateTime(request.updated_at),
                        })}
                    </Text>
                ) : null}
            </View>
        );
    }

    return (
        <View className="gap-0 pl-4">
            {events.map((event, index) => {
                const isLast = index === events.length - 1;
                const detail = getEventDetailText(event, (status) =>
                    t(`leave.status.${status}`),
                );

                return (
                    <View key={event.id} className="flex-row gap-3">
                        <View className="items-center">
                            <View
                                className={cn(
                                    "mt-1 size-2.5 rounded-full",
                                    getLeaveRequestEventDotClassName(event.type),
                                )}
                            />
                            {!isLast ? (
                                <View className="my-1 w-px flex-1 bg-border" />
                            ) : null}
                        </View>
                        <View
                            className={cn(
                                "flex-1 gap-0.5 pb-3",
                                isLast && "pb-0",
                            )}
                        >
                            <Text
                                className="text-sm"
                                testID={
                                    TEST_IDS[
                                        TEST_IDS
                                            .REQUESTS_LIST_ITEM_PROGRESS_EVENT
                                    ]
                                }
                            >
                                {t(`leave.events.${event.type}`)}
                            </Text>
                            <Text variant="muted" className="text-xs">
                                {formatDisplayDateTime(event.created_at)}
                            </Text>
                            {detail ? (
                                <Text variant="muted" className="mt-1">
                                    {detail}
                                </Text>
                            ) : null}
                        </View>
                    </View>
                );
            })}
        </View>
    );
}
